import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styles from "./Navbar.module.css";

type DropdownName = "features" | "resources";

const Navbar = ({ reserveDesktopSpace = false }: { reserveDesktopSpace?: boolean }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [openDropdown, setOpenDropdown] = useState<DropdownName | null>(null);
  const [menuRadiusActive, setMenuRadiusActive] = useState(false); // Delayed state for border radius
  const router = useRouter();

  const isCompactViewport = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 1024px)").matches;

  useEffect(() => {
    const handleScroll = () => {
      // Morph over the first 80px of scroll
      const maxScroll = 80;
      const progress = Math.min(window.scrollY / maxScroll, 1);
      setScrollProgress(progress);
    };
    
    handleScroll(); // Check initial state
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (isMenuOpen && window.innerWidth > 1024) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isMenuOpen) setOpenDropdown(null);
  };

  // Sync border radius state - immediate on open, delayed on close
  useEffect(() => {
    if (isMenuOpen) {
      setMenuRadiusActive(true);
    } else {
      const timeout = setTimeout(() => setMenuRadiusActive(false), 200); // Match menu close animation
      return () => clearTimeout(timeout);
    }
  }, [isMenuOpen]);

  const scrollToSection = (id: string, offsetY = 0) => {
    const target = document.getElementById(id);
    if (target) {
      const top = target.getBoundingClientRect().top + window.scrollY - offsetY;
      window.scrollTo({ top, behavior: "smooth" });
    } else {
      router.push(`/#${id}`);
    }
  };

  // Derived values from scroll progress (0 to 1)
  const isFullyScrolled = scrollProgress === 1;
  
  // Interpolate values based on scroll progress for smooth morph
  const backgroundOpacity = 1 - (scrollProgress * 0.15); // 1 -> 0.85
  const shadowOpacity = scrollProgress * 0.08; // 0 -> 0.08
  const shadowOpacity2 = scrollProgress * 0.05; // 0 -> 0.05
  const blurAmount = scrollProgress * 20; // 0 -> 20px
  const borderOpacity = 0.8 - (scrollProgress * 0.3); // 0.8 -> 0.5
  const topPosition = 44 - (scrollProgress * 24); // 44px (2.75rem) -> 20px as you scroll
  
  // Border radius logic:
  // - Menu closed: 50px pill (interpolates to 0 when scrolled)
  // - Menu open: 20px to match dropdown (interpolates to 0 when scrolled)
  // Uses menuRadiusActive (delayed on close) so radius doesn't change until menu finishes closing
  const pillRadius = 50 * (1 - scrollProgress);
  const menuOpenRadius = 20 * (1 - scrollProgress);
  const mobileBorderRadius = menuRadiusActive ? menuOpenRadius : pillRadius;
  
  // Menu gap: 10px at scroll 0, 0px when fully scrolled
  const menuGap = 10 * (1 - scrollProgress);
  
  // Helper to get offset based on device type
  const getScrollOffset = (mobileOffset: number, tabletOffset: number, desktopOffset: number) => {
    if (typeof window === "undefined") return desktopOffset;
    if (window.matchMedia("(max-width: 600px)").matches) return mobileOffset;
    if (window.matchMedia("(max-width: 1024px)").matches) return tabletOffset;
    return desktopOffset;
  };
  
  const handleDropdownToggle = (dropdown: DropdownName) => {
    setOpenDropdown((current) => current === dropdown ? null : dropdown);
  };

  // Close mobile/tablet menu after navigation
  const closeMobileMenu = () => {
    if (isCompactViewport()) {
      setIsMenuOpen(false);
    }
    setOpenDropdown(null);
  };

  const navContainerClasses = [
    styles.navContainer,
    isFullyScrolled ? styles.navbarScrolled : styles.navbarNotScrolled,
    reserveDesktopSpace ? styles.reservedDesktop : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={styles.navbar}>
      {/* Fixed spacer to account for fixed navbar */}
      <div style={{ height: "120px" }} />
      
      <nav
        className={navContainerClasses}
        style={{
          position: "fixed",
          top: `${topPosition}px`,
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: `rgba(255, 255, 255, ${backgroundOpacity})`,
          backdropFilter: blurAmount > 0 ? `blur(${blurAmount}px) saturate(${100 + scrollProgress * 80}%)` : "none",
          WebkitBackdropFilter: blurAmount > 0 ? `blur(${blurAmount}px) saturate(${100 + scrollProgress * 80}%)` : "none",
          boxShadow: `0 4px 30px rgba(0, 0, 0, ${shadowOpacity}), 0 1px 3px rgba(0, 0, 0, ${shadowOpacity2})`,
          borderColor: `rgba(211, 218, 230, ${borderOpacity})`,
          // Responsive geometry lives in CSS so the server and the browser use
          // the same media query on the very first frame.
          "--mobile-nav-width": `calc(100% - ${48 * (1 - scrollProgress)}px)`,
          "--mobile-nav-radius": `${mobileBorderRadius}px`,
          "--menu-gap": `${menuGap}px`,
          "--menu-border-radius": `${mobileBorderRadius}px`,
        } as React.CSSProperties}
      >
        {/* Logo */}
        <Link href="/">
          <a className={styles.logo}>
            <img
              src="/assets/brand/PrimaryLogoOptimized.svg"
              alt="LifeShack"
              width="944"
              height="281"
              className={styles.logoImage}
            />
          </a>
        </Link>

        {/* Hamburger Menu */}
        <button
          onClick={toggleMenu}
          className={`${styles.hamburger} ${isMenuOpen ? styles.hamburgerOpen : ""}`}
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
        >
          <span className={styles.hamburgerIcon}>
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
          </span>
        </button>

        {/* Navigation Links */}
        <div className={`${styles.navLinks} ${isMenuOpen ? styles.navLinksMenuOpen : ""}`}>
          <a
            href="#process"
            className={styles.navLink}
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("process", getScrollOffset(25, 425, 325));
              closeMobileMenu();
            }}
          >
            Our Process
          </a>
          
          <a
            href="#plans"
            className={styles.navLink}
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("plans");
              closeMobileMenu();
            }}
          >
            Pricing
          </a>

          {/* Features Dropdown */}
          <div
            className={`${styles.dropdown} ${openDropdown === "features" ? styles.dropdownOpen : ""}`}
            onMouseEnter={() => !isCompactViewport() && setOpenDropdown("features")}
            onMouseLeave={() => !isCompactViewport() && setOpenDropdown(null)}
          >
            <button
              type="button"
              className={styles.dropdownTrigger}
              onClick={() => handleDropdownToggle("features")}
              aria-expanded={openDropdown === "features"}
              aria-controls="features-menu"
              aria-haspopup="true"
            >
              Features
              <span className={styles.dropdownIcon}>▼</span>
            </button>
            <div id="features-menu" className={styles.dropdownMenu}>
              <div className={styles.dropdownCard}>
                <div className={styles.dropdownHeader}>
                  <span className={styles.dropdownHeaderText}>Product Features</span>
                </div>
                <Link href="/ai-auto-apply/">
                  <a className={styles.dropdownItem} onClick={closeMobileMenu}>Auto Apply</a>
                </Link>
                <Link href="/ai-resume/">
                  <a className={styles.dropdownItem} onClick={closeMobileMenu}>Resume Builder</a>
                </Link>
                <Link href="/ai-cover-letter/">
                  <a className={styles.dropdownItem} onClick={closeMobileMenu}>Cover Letter Builder</a>
                </Link>
              </div>
              <div className={styles.dropdownArrow} />
            </div>
          </div>

          {/* Resources Dropdown */}
          <div
            className={`${styles.dropdown} ${openDropdown === "resources" ? styles.dropdownOpen : ""}`}
            onMouseEnter={() => !isCompactViewport() && setOpenDropdown("resources")}
            onMouseLeave={() => !isCompactViewport() && setOpenDropdown(null)}
          >
            <button
              type="button"
              className={styles.dropdownTrigger}
              onClick={() => handleDropdownToggle("resources")}
              aria-expanded={openDropdown === "resources"}
              aria-controls="resources-menu"
              aria-haspopup="true"
            >
              Resources
              <span className={styles.dropdownIcon}>▼</span>
            </button>
            <div id="resources-menu" className={styles.dropdownMenu}>
              <div className={styles.dropdownCard}>
                <div className={styles.dropdownHeader}>
                  <span className={styles.dropdownHeaderText}>Explore Resources</span>
                </div>
                <a href="/resources/" className={styles.dropdownItem} onClick={closeMobileMenu}>
                  Blog
                </a>
                <Link href="/roi-calculator-job-search/">
                  <a className={styles.dropdownItem} onClick={closeMobileMenu}>ROI Calculator</a>
                </Link>
                <Link href="/compare/">
                  <a className={styles.dropdownItem} onClick={closeMobileMenu}>Compare LifeShack</a>
                </Link>
                <Link href="/best-ai-auto-apply-tools/">
                  <a className={styles.dropdownItem} onClick={closeMobileMenu}>Best AI Auto-Apply Tools</a>
                </Link>
              </div>
              <div className={styles.dropdownArrow} />
            </div>
          </div>

          <a
            href="#faqs"
            className={styles.navLink}
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("faqs", getScrollOffset(35, 35, 50));
              closeMobileMenu();
            }}
          >
            FAQ
          </a>

          <Link href="/signin/">
            <a className={styles.navLink} onClick={closeMobileMenu}>Sign In</a>
          </Link>

          {/* Mobile Sign Up */}
          <Link href="/signup/">
            <a
              className={`${styles.ctaButton} ${styles.ctaButtonMobile} ${styles.hideDesktop}`}
              onClick={closeMobileMenu}
            >
              Start for Free
            </a>
          </Link>
        </div>

        {/* Desktop CTA */}
        <div className={styles.hideMobile}>
          <Link href="/signup/">
            <a className={styles.ctaButton}>Start for Free</a>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
